/**
 * 前回ログインアカウントの記録・参照（F2 facade 系）。
 * 認証成立を onAuthStateChanged で観測し、IDトークンの sign_in_provider から
 * 実際に使われたプロバイダを判定して localStorage に残す。
 * 表示判定・UI は components/auth/LastLoginRecommendCard.vue 側。
 */
import { computed, shallowRef, type ComputedRef, type ShallowRef } from 'vue'
import {
  getFirebaseAuth,
  onAuthStateChanged,
  type User
} from '@/infra/firebase/auth'
import {
  type LastLoginAccount,
  isDismissCoolingDown,
  providerFromSignInProvider,
  readDismissedAt,
  readLastLoginAccount,
  writeDismissedAt,
  writeLastLoginAccount
} from '@/utils/auth/lastLoginAccount'

const account: ShallowRef<LastLoginAccount | null> = shallowRef(
  readLastLoginAccount()
)
const dismissedAt = shallowRef(readDismissedAt())

let captureInstalled = false

async function captureFromUser(user: User): Promise<void> {
  try {
    const tokenResult = await user.getIdTokenResult()
    const provider = providerFromSignInProvider(tokenResult.signInProvider)
    if (!provider) return
    const next: LastLoginAccount = {
      provider,
      displayName: user.displayName || '',
      email: user.email || '',
      photoURL: user.photoURL || '',
      savedAt: Date.now()
    }
    writeLastLoginAccount(next)
    account.value = next
  } catch (e) {
    // トークン取得失敗時は前回の記録を保持したままにする
    console.error('[useLastLoginAccount] capture failed', e)
  }
}

function ensureCapture(): void {
  if (captureInstalled) return
  captureInstalled = true
  onAuthStateChanged(getFirebaseAuth(), (user) => {
    if (user) {
      captureFromUser(user)
    }
  })
}

export type UseLastLoginAccountReturn = {
  /** 前回ログインの記録（無ければ null） */
  account: ShallowRef<LastLoginAccount | null>
  /** ×で閉じてから再表示抑止期間内か */
  isDismissed: ComputedRef<boolean>
  dismiss: () => void
}

export function useLastLoginAccount(): UseLastLoginAccountReturn {
  ensureCapture()

  const isDismissed = computed(() =>
    isDismissCoolingDown(dismissedAt.value, Date.now())
  )

  function dismiss(): void {
    const at = Date.now()
    writeDismissedAt(at)
    dismissedAt.value = at
  }

  return { account, isDismissed, dismiss }
}
